<script setup lang="ts">
import type { AppFunnelStep, Tip } from '~/types/commons'

definePageMeta({
  layout: 'empty',
  middleware: [
    'authenticated-to-signup',
    function (to) {
      const { name, params, query } = to
      if (import.meta.server) {
        if (name !== 'onboarding-step') {
          return navigateTo({ name: 'onboarding-step', params, query })
        }
      }
      if (name === 'onboarding') {
        return navigateTo({ name: 'onboarding-step', params, query })
      }
    },
  ],
})

const { t } = useI18n()

const steps: AppFunnelStep[] = [
  {
    id: 'start',
    routeRawName: 'onboarding-step',
    idForm: 'form-onboarding',
    hideWizzard: true,
    title: null,
    isAvailable: true,
  },
  {
    id: 'vehicle-type',
    routeRawName: 'onboarding-step-vehicle-type',
    idForm: 'form-onboarding-vehicle-type',
    isAvailable: true,
    title: null,
  },
  {
    id: 'vehicle-properties',
    routeRawName: 'onboarding-step-vehicle-properties',
    idForm: 'form-onboarding-vehicle-properties',
    isAvailable: true,
    title: null,
  },
  {
    id: 'vehicle-location',
    routeRawName: 'onboarding-step-vehicle-location',
    idForm: 'form-onboarding-vehicle-location',
    isAvailable: true,
    title: null,
  },
  {
    id: 'vehicle-pictures',
    routeRawName: 'onboarding-step-vehicle-pictures',
    idForm: 'form-onboarding-vehicle-pictures',
    isAvailable: true,
    title: null,
  },
  {
    id: 'product-price',
    routeRawName: 'onboarding-step-product-price',
    idForm: 'form-onboarding-product-price',
    isAvailable: true,
    title: null,
  },
  {
    id: 'end',
    routeRawName: 'onboarding-step-end',
    idForm: 'form-onboarding-end',
    hideWizzard: true,
    title: null,
    isAvailable: true,
  },
]

const route = useRoute()
const { routing } = storeToRefs(useRoutingStore())

const tipList = computed(() => {
  const list: Tip[] = []

  switch (route.name) {
    case 'onboarding-step':
      list.push(
        {
          title: t('components.ysc_tip.tip_onboarding_welcome.title'),
          description: t('components.ysc_tip.tip_onboarding_welcome.description_html'),
        },
      )
      break
    case 'onboarding-step-vehicle-type':
      list.push(
        {
          title: t('commons.tip'),
          description: t('components.ysc_tip.tip_onboarding_vehicle_registration.description_html'),
          variant: 'warning',
        },
      )
      break
    case 'onboarding-step-vehicle-properties':
      list.push(
        {
          title: t('components.ysc_tip.tip_onboarding_documents.title'),
          description: t('components.ysc_tip.tip_onboarding_documents.description_html'),
        },
        {
          title: t('components.ysc_tip.tip_onboarding_data_privacy_registration.title'),
          description: t('components.ysc_tip.tip_onboarding_data_privacy_registration.description_html'),
        },
      )
      break
    case 'onboarding-step-vehicle-location':
      list.push(
        {
          title: t('components.ysc_tip.tip_onboarding_data_privacy_location.title'),
          description: t('components.ysc_tip.tip_onboarding_data_privacy_location.description_html'),
        },
      )
      break
    case 'onboarding-step-vehicle-pictures':
      list.push(
        {
          title: t('components.ysc_tip.tip_onboarding_picture_size.title'),
          description: t('components.ysc_tip.tip_onboarding_picture_size.description_html'),
        },
      )
      break
    case 'onboarding-step-product-price':
      list.push(
        {
          title: t('commons.tip'),
          description: t('components.ysc_tip.tip_onboarding_price_modification.description_html'),
          variant: 'warning',
        },
        {
          title: t('commons.information'),
          description: t('components.ysc_tip.tip_onboarding_price_info.description_html'),
        },
      )
      break
    case 'onboarding-step-end':
      list.push(
        {
          title: t('commons.information'),
          description: t('components.ysc_tip.tip_onboarding_end_moderate.description_html'),
        },
        {
          title: t('commons.tip'),
          description: t('components.ysc_tip.tip_onboarding_end_answers.description_html'),
        },
        {
          title: t('commons.question'),
          description: 'components.ysc_tip.tip_onboarding_faq.description_dynamic',
          interpolated: {
            key: 'faq_link_external_string',
            text: t('components.ysc_tip.tip_onboarding_faq.faq_link_external.text'),
            link: routing.value?.faq_home,
          },
        },
      )
      break
  }
  return list
})

const { isSavingCamperVehicle } = storeToRefs(useCamperStore())
const { start, finish } = useLoadingIndicator()
watch(isSavingCamperVehicle, (val) => {
  if (val) {
    start()
    return
  }
  finish()
})

const { loading, currentStep, currentStepIndex, indexedStepsCount, isFirstStep, isLastStep, onStepCancel, onStepSuccess } = useAppFunnel({ steps })

provide(appFunnelKey, { loading, currentStep, onStepSuccess })

useSeoText({
  title: () => currentStep.value?.title ?? '',
})

const showSupersizingBanner = computed(() => route.query.from === 'goboony')

const closeSuperSizingBanner = async () => {
  await navigateTo({ query: { ...route.query, from: undefined } })
}
</script>

<template>
  <div>
    <YscLoadingBar />
    <AppSearchSupersizingBanner
      v-if="showSupersizingBanner"
      :display-read-more-link="false"
      @close="closeSuperSizingBanner"
    />
    <AppFunnel
      :current-step="currentStep"
      :current-step-index="currentStepIndex - 1"
      :steps-count="indexedStepsCount - 2"
      show-wizzard-step-count
    >
      <template
        v-if="currentStep"
        #main
      >
        <NuxtPage />
      </template>

      <template #aside>
        <YscTipList
          class="pt-12"
          :tip-list="tipList"
          :border="false"
        />
      </template>

      <template
        v-if="currentStep && !currentStep.hideFooter"
        #footer
      >
        <div
          v-if="isFirstStep || isLastStep"
          class="w-full text-center"
        >
          <button
            class="btn btn-primary"
            type="submit"
            :form="currentStep.idForm"
          >
            <template v-if="isFirstStep">
              {{ $t('commons.actions.create_new_camper') }}
            </template>
            <template v-else>
              {{ $t('commons.actions.finish') }}
            </template>
          </button>
        </div>
        <template v-else>
          <button
            v-if="onStepCancel"
            class="btn btn-secondary"
            :disabled="isFirstStep"
            @click="onStepCancel"
          >
            {{ $t('commons.actions.back') }}
          </button>

          <YscButtonSubmit
            :form="currentStep.idForm"
            :loading="isSavingCamperVehicle"
          >
            {{ $t('commons.actions.next') }}
          </YscButtonSubmit>
        </template>
      </template>
    </AppFunnel>
  </div>
</template>
